import { DateTime } from 'luxon';
import { MutableState } from '../State';
import {
  getTreasureExpressDifficultySetting,
  isTreasureExpressAvailable,
} from './treasureExpress.getters';
import {
  treasureExpressEventDuration,
  treasureExpressExpirationTime,
  treasureExpressSchedule,
  treasureExpressTicketRewards,
} from './treasureExpress.ruleset';

export function initializeTreasureExpressEvent(
  state: MutableState,
  now: number,
): void {
  // No active event, no score.
  const currentEventId = treasureExpressSchedule.getID(now);
  if (!currentEventId) return;

  // Initialize the event if it doesn't exist.
  if (state.treasureExpress?.entries[currentEventId]) return;

  state.treasureExpress.entries[currentEventId] = {
    tickets: 0,
    claimedMilestones: 0,
    difficulty: getTreasureExpressDifficultySetting(state, now),
  };
}

/**
 * Grant tickets to the user based on the current attempt.
 * @param state The mutable state.
 * @param now The current time.
 */
export function addTreasureExpressTickets(
  state: MutableState,
  now: number,
): void {
  // Event not available, bail.
  if (!isTreasureExpressAvailable(state, now)) {
    return;
  }

  // No active event, no score.
  const currentEventId = treasureExpressSchedule.getID(now);
  if (!currentEventId) return;

  initializeTreasureExpressEvent(state, now);

  // Calculate the amount of tickets to reward.
  const attempt = Math.min(
    state.match2.losingStreak,
    treasureExpressTicketRewards.length - 1,
  );
  const ticketRewards = treasureExpressTicketRewards[attempt];
  state.treasureExpress.entries[currentEventId].tickets += ticketRewards;
}

/**
 * Cleanup old treasure express entries
 * @param state
 * @param now
 * @returns
 */
export function cleanupOldTreasureExpressEntries(
  state: MutableState,
  now: number,
): void {
  // Loop through all entries and remove the ones that are older than the expiration time.
  for (const eventId of Object.keys(state.treasureExpress.entries)) {
    // Event id is actually a timestamp in ISO format
    const eventStartTime = DateTime.fromISO(eventId, {
      zone: 'utc',
    }).toMillis();

    const eventEndTime =
      eventStartTime +
      treasureExpressEventDuration +
      treasureExpressExpirationTime;

    // If the event has expired, remove it from the state.
    if (now > eventEndTime) {
      delete state.treasureExpress.entries[eventId];
    }
  }
}
